import { loadCompanyCensusEnv } from "@/env";
import { createApiClient } from "@cakewalk/frontend-api";

/**
 * Pre-login clients for the company-census "create account" flow.
 *
 * Both hit PUBLIC platform-API endpoints (no Cognito session yet):
 *   - eligibility gate (`/api/quote-census/eligibility`) — db-service answers
 *     whether the email is an existing OWNER; only then may we create a login.
 *   - account creation (`/api/quote-census/account`) — Cognito-ONLY. We already
 *     hold the owner's name / phone / company, so this sends just email +
 *     password; the platform API pulls the rest and provisions the auth user
 *     (a verified phone enables SMS 2FA on first sign-in).
 */

function baseUrl(): string {
  return loadCompanyCensusEnv().VITE_API_BASE_URL.replace(/\/$/, "");
}

function publicClient() {
  return createApiClient({ baseUrl: baseUrl(), requireAuth: false });
}

export interface CensusEligibility {
  exists: boolean;
  isOwner: boolean;
}

/** Owner-only gate: does this email exist in the old tables as the owner? */
export function checkCensusEligibility(email: string): Promise<CensusEligibility> {
  return publicClient().get<CensusEligibility>(
    `/api/quote-census/eligibility?email=${encodeURIComponent(email)}`,
  );
}

export interface CensusLoginPrecheck {
  /** The email is an existing business owner in our operational records. */
  isKnownOwner: boolean;
  /** A Cognito login already exists for the email. */
  hasLogin: boolean;
  /** The email maps to any contact on file. When false → prospect self-signup. */
  existsInDb: boolean;
}

/**
 * Classify a failed sign-in: when a known owner has no login yet, the login
 * step sends them into activation ("your data's here, you just never set a
 * password") instead of a wrong-password error.
 */
export function censusLoginPrecheck(email: string): Promise<CensusLoginPrecheck> {
  return publicClient().post<CensusLoginPrecheck>("/api/quote-census/auth/login-precheck", {
    email: email.trim(),
  });
}

export interface CreateCensusAccountInput {
  email: string;
  password: string;
}

export interface CreateCensusAccountResponse {
  success: boolean;
  sub: string;
}

/**
 * Create the Cognito login for an existing owner. Only email + password — every
 * other attribute comes from the records we already have.
 */
export function createCensusAccount(
  input: CreateCensusAccountInput,
): Promise<CreateCensusAccountResponse> {
  return publicClient().post<CreateCensusAccountResponse>("/api/quote-census/account", {
    email: input.email,
    password: input.password,
  });
}

export interface CreateCensusProspectInput {
  email: string;
  firstName: string;
  lastName: string;
  /** Business name — becomes the name of the lead company we create. */
  companyName: string;
  phone: string;
  password: string;
}

/**
 * Provision a brand-new PROSPECT: for an email not on file, creates the company
 * + primary contact and a Cognito login in the `prospect` group only (census
 * access, no cockpit/wallet). Collects name + company name (the new company's
 * name) + mobile phone (for SMS 2FA) + password.
 */
export function createCensusProspect(
  input: CreateCensusProspectInput,
): Promise<CreateCensusAccountResponse> {
  return publicClient().post<CreateCensusAccountResponse>("/api/quote-census/prospect", {
    email: input.email.trim(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    companyName: input.companyName.trim(),
    phone: input.phone,
    password: input.password,
  });
}
